import { Wizard, WizardStep, WizardStepRenderParams } from '@factorialco/deprecated-design-system'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileUploader from 'modules/documents/components/BulkFiles/_steps/fileUploader'
import SplittingSettings from 'modules/documents/components/BulkFiles/_steps/splittingSettings'
import UploadSettings from 'modules/documents/components/BulkFiles/_steps/uploadSettings'
import filesStore from 'modules/documents/components/Documents/store'
import { useCreateUploadProcess } from 'modules/documents/hooks/useCreateUploadProcess'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { ALL_UPLOAD_TYPES } from 'modules/documents/lib/constants'

import ManualSettings from '../_steps/manualSettings'
import { WizardData } from './types'

interface Props {
  onClose: () => void
  onDeleteFile: (i: number) => void
  onDropFiles: (files: File[]) => void
  onUploadFinished: () => Promise<unknown>
  folders: Folder[]
  isReadOnly: boolean
  wizardData: WizardData
  selectedFolder?: Folder
}

const DocumentLocalStepsModal = ({
  onClose,
  onDeleteFile,
  onDropFiles,
  onUploadFinished,
  isReadOnly,
  folders,
  wizardData,
  selectedFolder,
}: Props) => {
  const { createUploadProcess } = useCreateUploadProcess()
  const checkUploadedFiles = () => filesStore.files.length > 0
  const checkSubmit = (
    uploadType: (typeof ALL_UPLOAD_TYPES)[keyof typeof ALL_UPLOAD_TYPES],
    combineFileName: string | undefined
  ) => {
    return (
      checkUploadedFiles() &&
      ((uploadType !== 'split' && uploadType !== 'combine') || combineFileName !== '')
    )
  }

  const onSubmit = async ({
    distributionWorkflow,
    uploadType,
    folderId,
    combineFileName,
    numberSplitPages,
    esignatureRequest,
    additionalSignersAccessIds,
  }: WizardData) => {
    const result = await createUploadProcess({
      distributionWorkflow,
      files: filesStore.files,
      uploadType,
      folderId,
      combineFileName,
      numberSplitPages,
      esignatureRequest,
      additionalSignersAccessIds,
    })

    const handled = handleResult(result, (errors) => {
      const errorMessages = Object.values(errors)
      const errorMessage = errorMessages && errorMessages[0]

      if (errorMessage) app.setFlash({ type: 'wrong', message: errorMessage })
    })

    if (handled.success) {
      await onUploadFinished()
    }

    return handled
  }

  const splittingSettings = (props: WizardStepRenderParams<WizardData>) => {
    return wizardData.distributionWorkflow === 'payslips' ? (
      <ManualSettings {...props} />
    ) : (
      <SplittingSettings {...props} />
    )
  }

  const fileUploaderStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.form.documents'),
    isCompleted: () => checkUploadedFiles(),
    nextText: i18n.t('common.next'),
    render: (props) => (
      <FileUploader
        {...props}
        isReadOnly={isReadOnly}
        onDeleteFile={onDeleteFile}
        onDropFiles={onDropFiles}
      />
    ),
  }

  const splittingStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.sidepanel.splitting'),
    isCompleted: () => checkUploadedFiles(),
    nextText: i18n.t('common.next'),
    render: (props) => splittingSettings(props),
  }

  const settingsStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.sidepanel.settings'),
    isCompleted: ({ uploadType, combineFileName }) => checkSubmit(uploadType, combineFileName),
    nextText: i18n.t('split_documents.sidepanel.upload_files'),
    render: (props) => (
      <UploadSettings {...props} folders={folders} selectedFolder={selectedFolder} />
    ),
  }

  return (
    <Wizard.Modal
      title={i18n.t('split_documents.summary.title_v2')}
      icon="common-file-text-upload"
      initialData={wizardData}
      onSubmit={onSubmit}
      onClose={onClose}
      steps={[fileUploaderStep, splittingStep, settingsStep]}
    />
  )
}

export default DocumentLocalStepsModal
